import { useState } from "react";
import { NavLink, Route, Routes, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, ListChecks, Package, Flag, ShieldCheck, UserCog, Settings, LogOut, Loader2, ScrollText, Repeat, Receipt, Image as ImageIcon, BarChart3 } from "lucide-react";
import ContentPage from "@/admin/AdminContent";
import StaffPage from "@/admin/AdminStaff";
import StatsPage from "@/admin/AdminStats";
import ProposalsPage from "@/admin/AdminProposals";
import OrdersPage from "@/admin/AdminOrders";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoader } from "@/components/RequireAuth";
import { Dashboard, UsersPage, ListingsPage, ReportsPage, PrivacyPage, SettingsPage, AuditPage } from "@/admin/AdminPages";
import OutletPage from "@/admin/AdminOutlet";

export const ADMIN_BASE = `/${process.env.REACT_APP_ADMIN_PATH}`;

const AdminLogin = () => {
  const { signedIn } = useAuth();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/admin/login", f); signedIn(data); } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#1E1B2E] px-4" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-3xl bg-white p-8">
        <img src="/juppo-logo.png" alt="Juppo" className="mx-auto h-16 object-contain" />
        <p className="text-center font-display text-lg font-bold">Area riservata</p>
        <div><label className="label">Username o email</label><input required className="field" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} data-testid="admin-login-identifier" autoComplete="username" /></div>
        <div><label className="label">Password</label><input required type="password" className="field" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-login-password" autoComplete="current-password" /></div>
        {err && <p className="text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="btn-violet w-full !py-3" data-testid="admin-login-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Accedi</button>
      </form>
    </div>
  );
};

const NAV = [
  ["", "Dashboard", LayoutDashboard, "dashboard"], ["statistiche", "Incassi e statistiche", BarChart3, "stats"], ["utenti", "Utenti e ruoli", Users, "users"],
  ["annunci", "Annunci e moderazione", ListChecks, "listings"], ["proposte", "Proposte e scambi", Repeat, "proposals"], ["ordini", "Ordini e rimborsi", Receipt, "orders"],
  ["outlet", "Outlet e inventario", Package, "outlet"], ["contenuti", "Home e promo", ImageIcon, "content"], ["segnalazioni", "Segnalazioni", Flag, "reports"],
  ["privacy", "Richieste privacy", ShieldCheck, "privacy"], ["staff", "Utenti backend", UserCog, "admin"], ["impostazioni", "Impostazioni", Settings, "settings"], ["audit", "Registro attività", ScrollText, "audit"],
];
const ROLE_NAME = { admin: "amministratore", moderator: "moderatore", operator: "operatore" };

export default function AdminApp() {
  const { user, logout } = useAuth();
  if (user === null) return <PageLoader />;
  if (!user || !["admin", "moderator", "operator"].includes(user.role)) return <AdminLogin />;
  const isAdmin = user.role === "admin";
  const perms = new Set(user.staff_permissions || []);
  const can = (p) => (p === "admin" ? isAdmin : isAdmin || perms.has(p));
  const nav = NAV.filter((n) => can(n[3]));
  const pages = { "": <Dashboard isAdmin={isAdmin} />, statistiche: <StatsPage />, utenti: <UsersPage isAdmin={isAdmin} />, annunci: <ListingsPage />, proposte: <ProposalsPage />,
    ordini: <OrdersPage isAdmin={can("stats")} />, outlet: <OutletPage />, contenuti: <ContentPage />, segnalazioni: <ReportsPage />, privacy: <PrivacyPage />,
    staff: <StaffPage me={user.user_id} />, impostazioni: <SettingsPage />, audit: <AuditPage /> };
  const home = nav[0] ? `${ADMIN_BASE}/${nav[0][0]}` : ADMIN_BASE;
  return (
    <div className="flex min-h-screen bg-[#F8F9FC]" data-testid="admin-app">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-[#1E1B2E] p-4 text-white md:flex">
        <div className="rounded-2xl bg-white p-2"><img src="/juppo-logo.png" alt="Juppo" className="mx-auto h-10 object-contain" /></div>
        <p className="mt-3 px-2 text-xs uppercase tracking-widest text-white/50">Pannello {ROLE_NAME[user.role]}</p>
        <nav className="mt-4 flex flex-1 flex-col gap-1">
          {nav.map(([p, label, Icon]) => (
            <NavLink key={p} end to={`${ADMIN_BASE}/${p}`} data-testid={`admin-nav-${p || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-[#FF7A6B] text-white" : "text-white/80 hover:bg-white/10"}`}>
              <Icon className="h-4 w-4" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3 text-sm"><p className="truncate px-2 font-semibold">{user.name}</p><p className="truncate px-2 text-xs text-white/50">{user.username} · {user.role}</p>
          <button onClick={logout} className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-white/80 hover:bg-white/10" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Esci</button></div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="no-scrollbar flex gap-1 overflow-x-auto bg-[#1E1B2E] p-2 md:hidden">
          {nav.map(([p, label]) => <NavLink key={p} end to={`${ADMIN_BASE}/${p}`} className={({ isActive }) => `shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-[#FF7A6B] text-white" : "text-white/80"}`}>{label}</NavLink>)}
          <button onClick={logout} className="shrink-0 px-3 text-xs text-white/80">Esci</button>
        </div>
        <main className="p-4 sm:p-8">
          <Routes>
            {nav.map(([p]) => (p === "" ? <Route key="index" index element={pages[""]} /> : <Route key={p} path={p} element={pages[p]} />))}
            <Route path="*" element={nav.length ? <Navigate to={home} replace /> : <p className="text-sm text-[#64748B]" data-testid="admin-no-perms">Nessuna sezione assegnata al tuo account: contatta un amministratore.</p>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
